'use client';

import { Copy, RefreshCw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { buildDeployDiagnosticsClipboardText, type BuildInfoFetchState } from '../lib/buildInfo';
import { copyTextToClipboard } from '../lib/clipboard';
import { cn } from '../lib/cn';
import { useDeployDiagnostics } from '../hooks/queries/useDeployDiagnostics';

type FieldKey = 'shortSha' | 'builtAt' | 'version';

const fieldKeys: FieldKey[] = ['shortSha', 'builtAt', 'version'];

const formatValue = (value: string) => value || 'unknown';

type DiagnosticsSectionProps = {
  title: string;
  state: BuildInfoFetchState | null;
  isLoading: boolean;
  labels: Record<FieldKey, string>;
  availableLabel: string;
  unavailableLabel: string;
};

const DiagnosticsSection = ({
  title,
  state,
  isLoading,
  labels,
  availableLabel,
  unavailableLabel,
}: DiagnosticsSectionProps) => {
  const isAvailable = state?.status === 'success';
  const buildInfo = isAvailable ? state.data : null;

  return (
    <section className="rounded-lg border border-base-300 bg-base-100 p-3">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold">{title}</h4>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[11px] font-medium',
            isAvailable ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning',
          )}
        >
          {isAvailable ? availableLabel : unavailableLabel}
        </span>
      </div>

      <dl className="mt-3 space-y-2 text-xs">
        {fieldKeys.map((key) => (
          <div key={key} className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-2">
            <dt className="text-[var(--color-fg-subtle)]">{labels[key]}</dt>
            <dd className="min-w-0 break-words font-mono text-[11px] text-base-content/80">
              {isLoading ? '...' : buildInfo ? formatValue(buildInfo[key]) : unavailableLabel}
            </dd>
          </div>
        ))}
      </dl>

      {state?.status === 'error' ? <p className="mt-2 text-xs text-warning">{state.errorMessage}</p> : null}
    </section>
  );
};

export default function DeployDiagnosticsPanel() {
  const t = useTranslations('profile.page.deployDiagnostics');
  const tCommon = useTranslations('common');
  const diagnosticsQuery = useDeployDiagnostics();
  const diagnostics = diagnosticsQuery.data ?? null;

  const labels: Record<FieldKey, string> = {
    shortSha: t('shortSha'),
    builtAt: t('builtAt'),
    version: t('version'),
  };

  const handleCopy = async () => {
    if (!diagnostics) {
      toast.error(t('copyUnavailable'));
      return;
    }

    try {
      await copyTextToClipboard(buildDeployDiagnosticsClipboardText(diagnostics));
      toast.success(t('copied'));
    } catch {
      toast.error(t('copyFailed'));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold">{t('title')}</h4>
        <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{t('description')}</p>
      </div>

      <div className="space-y-3">
        <DiagnosticsSection
          title={t('webTitle')}
          state={diagnostics?.web ?? null}
          isLoading={diagnosticsQuery.isLoading}
          labels={labels}
          availableLabel={t('available')}
          unavailableLabel={t('unavailable')}
        />
        <DiagnosticsSection
          title={t('apiTitle')}
          state={diagnostics?.api ?? null}
          isLoading={diagnosticsQuery.isLoading}
          labels={labels}
          availableLabel={t('available')}
          unavailableLabel={t('unavailable')}
        />
      </div>

      <div className="rounded-lg border border-base-300 bg-base-100 p-3">
        <span className="block text-xs font-medium text-[var(--color-fg-subtle)]">{t('apiUrl')}</span>
        <span className="mt-1 block break-all font-mono text-[11px] text-base-content/80">
          {diagnostics?.apiUrl ?? '...'}
        </span>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => void diagnosticsQuery.refetch()}
          disabled={diagnosticsQuery.isFetching}
        >
          <RefreshCw className={cn('h-4 w-4', diagnosticsQuery.isFetching && 'animate-spin')} aria-hidden="true" />
          {tCommon('actions.refresh')}
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => void handleCopy()}>
          <Copy className="h-4 w-4" aria-hidden="true" />
          {t('copy')}
        </button>
      </div>
    </div>
  );
}
